// saveUsersData.js
'use strict';
import { ADD_USERS, MERGE_ME_DATA } from '../ActionTypes.js';
import getMeData from '../selector/getMeData.js';
import getUserData from '../selector/getUserData.js';
import { getIsUserLevelBadge } from '../selector/getUserBadges.js';
import objectifyArrayById from '../resource/objectifyArrayById.js';
import getRemoteConfigData from '../selector/getRemoteConfigData.js';
import { USER_LEVEL_BADGE_PREFIX } from '../RemoteConfigKeys.js';

/**
 * @typedef UserData
 * @property {string} id - user's id.
 * @property {number} level - user's level.
 * @property {[]<string>} badges - user's badges.
 */

/**
 * Save users data
 * @kind action
 * @param {[]<UserData>} {users} - users data.
 * @return {Promise} Action promise.
 */
const saveUsersData =
  ({ users = [] } = {}) =>
  async (dispatch, getState) => {
    const meId = getMeData(getState(), 'id');
    const userLevelBadgePrefix = getRemoteConfigData(
      getState(),
      USER_LEVEL_BADGE_PREFIX
    );
    const userData = objectifyArrayById({
      array: users.map(({ id, level, badges = [], ...rest }) => {
        const data = {
          id,
          ...rest,
        };
        // Store level data for future backend support
        if (level !== undefined) {
          data.level = level;
        }
        if (level || badges.length > 0) {
          let nextBadges = [
            ...new Set([
              ...(getUserData(getState(), id, 'badges') || []),
              ...badges,
            ]),
          ];
          if (level) {
            nextBadges = nextBadges.filter(
              badge => !getIsUserLevelBadge({ userLevelBadgePrefix, badge })
            );
            nextBadges.push(`${userLevelBadgePrefix}${level}`);
          }
          data.badges = nextBadges;
        }
        return data;
      }),
    });
    if (userData[meId]) {
      const { id, ...restMeData } = userData[meId];
      // Because in most cases, only the user's level (not detailed level information) is available from the public API, we remove it here to avoid overwriting more complete level information already present in "me".
      // The backend only returns detailed level information in the private API.
      delete restMeData.level;
      dispatch({
        type: MERGE_ME_DATA,
        payload: restMeData,
      });
    }
    return dispatch({
      type: ADD_USERS,
      payload: {
        users: userData,
      },
    });
  };

export default saveUsersData;
